/**
 * Shared validation rules matching saturn-api-1 backend constraints.
 * Prevents 422 errors by catching invalid data before it reaches the API.
 */

export const SCHOOL_NAME_REGEX = /^[a-z0-9_]+$/;

// Backend: ^([ \u00c0-\u01ffa-zA-Z'`\-\u2012-\u2015\u2018-\u201b])+$
const USER_NAME_ALLOWED_CHARS =
  /^[ \u00C0-\u01FFa-zA-Z'`\-\u2012\u2013\u2014\u2015\u2018\u2019\u201A\u201B]+$/;

const HAS_DIGIT = /\d/;

export const LAT_RANGE = { min: -90, max: 90 } as const;
export const LNG_RANGE = { min: -180, max: 180 } as const;

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB - matches backend MAX_S3_PICTURE_UPLOAD_SIZE_BYTES

export function validateSchoolName(name: string): string | null {
  if (!name) return 'School ID is required';
  if (!SCHOOL_NAME_REGEX.test(name)) {
    return 'School ID must only contain lowercase letters, numbers, and underscores';
  }
  return null;
}

export function validateUserName(name: string, label: string): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return `Please provide a valid ${label}`;
  if (HAS_DIGIT.test(trimmed)) return `${label} cannot contain numbers`;
  if (!USER_NAME_ALLOWED_CHARS.test(trimmed)) return `${label} contains invalid characters`;
  return null;
}

export function validateLatitude(lat: number | undefined | null): string | null {
  if (lat == null || isNaN(lat)) return null;
  if (lat < LAT_RANGE.min || lat > LAT_RANGE.max) {
    return `Latitude must be between ${LAT_RANGE.min} and ${LAT_RANGE.max}`;
  }
  return null;
}

export function validateLongitude(lng: number | undefined | null): string | null {
  if (lng == null || isNaN(lng)) return null;
  if (lng < LNG_RANGE.min || lng > LNG_RANGE.max) {
    return `Longitude must be between ${LNG_RANGE.min} and ${LNG_RANGE.max}`;
  }
  return null;
}

export function validateTimeRange(startTime: string, endTime: string): string | null {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if (eh * 60 + em <= sh * 60 + sm) return 'End time must be after start time';
  return null;
}

export function validateDateOrder(
  startDate: Date | undefined,
  endDate: Date | undefined,
): string | null {
  if (!startDate || !endDate) return null;
  if (endDate < startDate) return 'End date must be after start date';
  return null;
}

export function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'File must be a PNG, JPG, or GIF image';
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `File size (${sizeMB}MB) exceeds the 2MB limit`;
  }
  return null;
}

export function validateDuplicateNames(
  names: string[],
  label: string,
): string | null {
  const seen = new Set<string>();
  for (const name of names) {
    const upper = name.toUpperCase();
    if (seen.has(upper)) return `Duplicate ${label} name: "${name}"`;
    seen.add(upper);
  }
  return null;
}
